import { useEffect, useRef, useState } from "react";
import { useParams, useLocation } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import { generatePDPMeta } from "@/utils/meta";
import {
  fetchPDPBidCenterDataSuccess,
  fetchPDPLatestBidCenterDataSuccess,
  resetPDPState,
  setPdpError,
  setIsNDAAccepted,
} from "@/redux/PDP/pdpSlice";
import { getPDPInitData, getLatestBidCenter, getBidCenterDetails, getCMS } from "@/api/PDP";
import { fetchPDPInitialLoadSuccess } from "@/redux/PDP/pdpSlice";
import { setListingId } from "@/bidFeature/propertyDetailSlice";
import useAuth from "@/components/common/header/useAuth";
import CommercialPDP from "@/components/pdp/commercial/CommercialPDP";
import ShimmerLoader from "@/components/pdp/ShimmerLoader";
import CommercialHeader from "@/components/pdp/commercial/CommercialHeader";
import MobileMenu from "@/components/common/header/MobileMenu";
import OtherLinkFooter from "@/components/common/footer/OtherLinkFooter";
import NewFooter from "@/components/common/footer/NewFooter";
import LoginModal from "@/components/common/user-credentials/LoginModal";
import { captureAttribution, consumeInternalNavigation } from "@/utils/attributionCapture";
import { trackCategoryPageView, getCategoryFromPropCategory } from "@/utils/categoryPageTrackingService";
import { trackButtonClick } from "@/utils/analytics";

const CommercialPropertyDetails = () => {
  const { id: rawId } = useParams();
  const id = rawId?.indexOf("-") > 0 ? rawId.slice(0, rawId.indexOf("-")) : rawId;
  const dispatch = useDispatch();
  const { isUserLoggedIn } = useAuth();

  const pdpInitData = useSelector((state) => {
    return state.pdp.pdpInitData;
  });
  const isPDPLoading = useSelector((state) => state.pdp.isPDPLoading);
  const pdpError = useSelector((state) => state.pdp.pdpError);

  const { pathname } = useLocation();
  const meta = generatePDPMeta({ pdpInitData: pdpInitData?.propertyDetails, pathname });

  // Tracking refs — capture internal nav flag early before it gets consumed
  const trackedPropertyId = useRef(null);
  const hasTrackedPageView = useRef(false);
  const isInternalNav = useRef(consumeInternalNavigation());
  // CMS data fetched at page level (parallel with PDP init) for faster bid card render
  const [pageCmsData, setPageCmsData] = useState(null);

  // Tracks whether the user clicked "Register for Tour" before logging in.
  // Kept here (page level) so it survives the CommercialPDP unmount/remount
  // that happens when the shimmer re-fetches PDP data after login.
  const [openTourModalOnLoad, setOpenTourModalOnLoad] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getCMS("pages/pdp").then((response) => {
      if (!cancelled) {
        setPageCmsData(response?.results?.[0]?.sections);
      }
    });
    return () => { cancelled = true; };
  }, []);

  // Fetch PDP data on mount or when property ID changes
  useEffect(() => {
    if (!id) return;

    dispatch(resetPDPState());

    const loadData = async () => {
      try {
        const data = await getPDPInitData(id);
        if (data && !data.error && data.propertyDetails && !data.errorMessage) {
          dispatch(fetchPDPInitialLoadSuccess(data));
          dispatch(setListingId(data?.propertyDetails));
          dispatch(setIsNDAAccepted(data?.ndaCommercialAccepted));

          // Fetch bid center data
          const listingId = data?.propertyDetails?.listingId;
          if (listingId) {
            const [bidCenterData, latestBidData] = await Promise.allSettled([
              getBidCenterDetails(listingId),
              getLatestBidCenter(listingId),
            ]);

            if (bidCenterData.status === "fulfilled" && bidCenterData.value) {
              dispatch(fetchPDPBidCenterDataSuccess({ ...bidCenterData.value, listingId }));
            }
            if (latestBidData.status === "fulfilled" && latestBidData.value) {
              dispatch(fetchPDPLatestBidCenterDataSuccess(latestBidData.value));
            }
          }
        } else {
          dispatch(setPdpError("Failed to load property details"));
        }
      } catch (error) {
        dispatch(setPdpError("Failed to load property details"));
      }
    };

    loadData();




  }, [id, dispatch, isUserLoggedIn]);

  // Track category page view for filter-interaction API (fires once per page load)
  useEffect(() => {
    // Reset tracking flag when navigating to a different property
    if (id !== trackedPropertyId.current) {
      hasTrackedPageView.current = false;
    }

    const propertyDetails = pdpInitData?.propertyDetails;
    if (!propertyDetails || hasTrackedPageView.current) return;

    // Stale data guard: ensure pdpInitData belongs to the current property
    const dataPropertyId = String(propertyDetails?.propertyId || propertyDetails?.listingId || "");
    if (id && dataPropertyId && !dataPropertyId.includes(id) && !id.includes(dataPropertyId)) {
      return; // Data is stale from a previous property, skip
    }

    // Capture traffic attribution (UTM params including SFMC) on commercial PDP entry
    captureAttribution();

    const status = propertyDetails?.commonAttributes?.status;
    const isPreview = status === "PACT" || status === "PACT_NON_COMMERCIAL";
    let categoryType = getCategoryFromPropCategory(
      propertyDetails?.commonAttributes?.propCategory || "COMMERCIAL"
    );
    if (categoryType && isPreview) {
      categoryType = `${categoryType},propertyStatus=${status}`;
    }

    if (categoryType) {
      trackCategoryPageView({
        categoryType,
        pageName: "PDP",
        propertyId: propertyDetails?.propertyId || id || null,
        isInternal: isInternalNav.current,
      });
    }

    // Fire GA4/GTM in parallel with the same correct data as the filter-interaction API
    trackButtonClick({
      eventName: "pdp_view",
      eventSource: "PDP COMMERCIAL",
      eventCategory: "NEW-PDP",
      eventLabel: `Category: ${categoryType} | Property id: ${propertyDetails?.propertyId || id || ''}`,
    });

    hasTrackedPageView.current = true;
    trackedPropertyId.current = id;
  }, [pdpInitData, id]);

  // Error state
  if (pdpError === "Failed to load property details") {
    return (
      <>
        <Helmet>
          <title>{meta?.title}</title>
          <meta name="description" content={meta?.description} />
          <meta name="keywords" content={meta?.metaKeyWords} />
        </Helmet>
        <CommercialHeader />
        <div className="container" style={{ minHeight: "60vh", paddingTop: 130, textAlign: "center" }}>
          <div className="mb20">
            <img src="/assets/images/pdp/property-notavailable.png" width="200" height="150" alt="Property not available" />
          </div>
          <span className="page_error_Txt pt20 mt20">We're sorry, but this property is currently unavailable.<br />We have plenty more, so look for your next home now.</span>
          <div style={{marginTop: "30px"}}>
            <a className="continueYourSearch" href="/auctions?listingType=AUCN">CONTINUE YOUR SEARCH</a>
          </div>
        </div>
        <OtherLinkFooter />
        <NewFooter />
      </>
    );
  }

  // Loading state
  if (!pdpError && (isPDPLoading || !pdpInitData?.propertyDetails)) {
    return (
      <>
        <Helmet>
          <title>{meta?.title}</title>
          <meta name="description" content={meta?.description} />
          <meta name="keywords" content={meta?.metaKeyWords} />
        </Helmet>
        <CommercialHeader />
        <ShimmerLoader />
        {/* <OtherLinkFooter /> */}
        <NewFooter />
      </>
    );
  }

  return (
    <div className="commercial-pdp-page">
      <Helmet>
        <title>{meta?.title}</title>
        <meta name="description" content={meta?.description} />
        <meta name="keywords" content={meta?.metaKeyWords} />
      </Helmet>
      <CommercialHeader />
      <MobileMenu />
      <main id="main-content">
        <CommercialPDP
          pdpInitData={pdpInitData}
          isUserLoggedIn={isUserLoggedIn}
          propertyId={id}
          pageCmsData={pageCmsData}
          openTourModalOnLoad={openTourModalOnLoad}
          onTourModalOnLoadConsumed={() => setOpenTourModalOnLoad(false)}
          onRegisterTourRequested={() => setOpenTourModalOnLoad(true)}
        />
      </main>
      {/* <OtherLinkFooter /> */}
      <NewFooter />
      <LoginModal />
    </div>
  );
};
//Adding logs

export default CommercialPropertyDetails;
